import React, { useContext, useEffect, useState, useRef } from 'react';
import { format, isToday, differenceInHours } from 'date-fns';
import haxios from '../lib/haxios';
import { GlobalContext } from '../context/GlobalContext';
import { RotateCw } from 'lucide-react';

const getQuoteCategory = (quoteDate) => {
    const now = new Date();
    if (!isToday(quoteDate)) return 'grey';
    const hoursDiff = differenceInHours(now, quoteDate);
    if (hoursDiff < 1) return 'green';
    if (hoursDiff < 2) return 'orange';
    return 'red';
};

const QuoteList = ({ selectedOperator }) => {
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const { uinfo } = useContext(GlobalContext);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [clickPosition, setClickPosition] = useState(null);
    const [filters, setFilters] = useState({ green: true, orange: true, red: true, grey: true });

    const fetchQuotes = async () => {
        setLoading(true);
        try {
            const response = await haxios.get(`/visiondashboard/quotes/${selectedOperator.Inits}`);
            setQuotes(response.data);
            setLastUpdated(new Date());
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQuotes();
        const interval = setInterval(fetchQuotes, 30000);
        return () => clearInterval(interval);
    }, [uinfo.Inits]);

    const handleRowClick = (quote, e) => {
        e.stopPropagation();
        if (selectedQuote?.Document === quote.Document) {
            setSelectedQuote(null);
            setClickPosition(null);
        } else {
            setSelectedQuote(quote);
            setClickPosition({ x: e.clientX, y: e.clientY });
        }
    };

    const categoryCounts = quotes.reduce((acc, quote) => {
        const category = getQuoteCategory(new Date(quote.DateTime));
        acc[category] = (acc[category] || 0) + 1;
        return acc;
    }, {});

    if (loading && !quotes.length) {
        return <div className="w-full p-4 text-center">Loading quotes data...</div>;
    }

    if (!quotes?.length) {
        return <div className="w-full p-4 text-center text-gray-500">No outstanding quotes found</div>;
    }

    return (
        <div className="w-full bg-white rounded-lg shadow relative">
            <div className="flex justify-between items-center px-4 py-3">
                <h2 className="font-bold">OUTSTANDING QUOTES</h2>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>Last updated: {format(lastUpdated, 'HH:mm:ss')}</span>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            fetchQuotes();
                        }}
                        className="p-2 hover:bg-gray-50 rounded-full"
                        disabled={loading}
                    >
                        <RotateCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>
            <div className="flex justify-between items-center px-4 py-3 bg-gray-200 font-bold">
                {['green', 'orange', 'red', 'grey'].map((cat) => (
                    <button
                        key={cat}
                        className={`px-4 py-2 rounded ${cat === 'green' ? 'bg-green-500' :
                            cat === 'orange' ? 'bg-orange-500' :
                                cat === 'red' ? 'bg-red-500' :
                                    'bg-gray-500'
                            } text-white`}
                        onClick={() => setFilters((prev) => ({ ...prev, [cat]: !prev[cat] }))}
                    >
                        {cat === 'green' ? '< 1 Hour' : cat === 'orange' ? '< 2 Hours' : cat === 'red' ? '> 2 Hours' : 'Older'}: {categoryCounts[cat] || 0}
                    </button>
                ))}
            </div>
            <div className="grid grid-cols-12 gap-4 p-3 text-sm font-semibold bg-gray-100">
                <div className="col-span-1"></div>
                <div className="col-span-2">Document</div>
                <div className="col-span-2">Date</div>
                <div className="col-span-4">Customer</div>
                <div className="col-span-2">Parts</div>
                <div className="col-span-1">Value</div>
            </div>
            <div className="max-h-96 overflow-y-auto">
                {quotes.map((quote) => {
                    const quoteDate = new Date(quote.DateTime);
                    const category = getQuoteCategory(quoteDate);
                    if (!filters[category]) return null;
                    return (
                        <div
                            key={quote.Document}
                            className={`grid grid-cols-12 gap-4 p-3 text-sm border-b cursor-pointer ${category === 'grey' ? 'bg-gray-300 text-gray-600' :
                                category === 'green' ? 'bg-green-900 text-white' :
                                    category === 'orange' ? 'bg-orange-700 text-white' :
                                        'bg-red-800 text-white'
                                }`}
                            onClick={(e) => handleRowClick(quote, e)}
                        >
                            <div className={`w-2 h-full ${category === 'green' ? 'bg-green-500' :
                                category === 'orange' ? 'bg-orange-500' :
                                    category === 'red' ? 'bg-red-500' :
                                        'bg-gray-500'
                                }`}></div>
                            <div className="col-span-2">{quote.Document}</div>
                            <div className="col-span-2">{format(quoteDate, 'dd/MM/yy HH:mm')}</div>
                            <div className="col-span-4 truncate">{quote.CustomerName}</div>
                            <div className="col-span-2">{quote.PartCount} items</div>
                            <div className="col-span-1">£{Number(quote.TotalValue).toFixed(2)}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default QuoteList;
